import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import SanitizedHTML from 'react-sanitized-html';
import TinderCard from 'react-tinder-card';
import styled, { css, keyframes } from 'styled-components';
import { IProviderItemView } from 'store/providers/providerTypes';
import { black } from 'utils/style/colors';

export type ItemsTinderSwipeDirection = 'left' | 'right' | 'up' | 'down';

export interface ItemsTinderStackRenderContext {
  index: number;
  total: number;
  isTop: boolean;
}

const rotate30WithRumble = keyframes`
  0% {
    transform: rotate(0deg) translate(0, 0);
  }
  50% {
    transform: rotate(1deg) ;
  }
  100% {
    transform: rotate(0deg) translate(0, 0);
  }
`;

export interface IItemsTinderStackProps {
  items: IProviderItemView[];
  title: string | undefined;
  className?: string;
  onSwipe?: (
    direction: ItemsTinderSwipeDirection,
    item: IProviderItemView
  ) => void;
  onCardLeftScreen?: (
    direction: ItemsTinderSwipeDirection,
    item: IProviderItemView
  ) => void;
  preventSwipe?: ItemsTinderSwipeDirection[];
  swipeThreshold?: number;
}

const StackContainer = styled.div`
  margin-bottom: 4rem;
`;

const StackHeader = styled.div`
  line-height: 24px;
  margin: 2rem auto;
  font-size: 20px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const StackRoot = styled.div`
  position: relative;
  max-width: 1024px;
  height: 100%;
  aspect-ratio: 16/9;
  margin: 0 auto;
  padding: 0.5rem;
  touch-action: none;
`;

const DefaultCard = styled.div`
  height: 100% !important;
  width: 100% !important;
  padding: 15px !important;
  display: flex !important;
  flex-direction: column !important;
  box-shadow: none !important;
  border-radius: 2.5rem !important;

  ${(props) =>
    props.isFirstCard
      ? css`
          animation: ${rotate30WithRumble} 2.5s ease-out forwards infinite;
          animation-iteration-count: infinite;
          transform-origin: center;
        `
      : ''}
`;

const DefaultCardContent = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  grid-gap: 0.5rem;
  padding-top: 1rem;
  color: ${black};
  & > * {
    height: 3rem;
  }
`;

const DefaultCardImage = styled.img`
  aspect-ratio: 16/9;
  border-radius: 1.5rem;
  object-fit: cover;
`;

const defaultRenderCard = (
  item: IProviderItemView,
  isFirstCard: boolean = false
) => {
  const title = item?.title || item?.name || '';
  const url = item.url || item.document?.url;
  return (
    <DefaultCard className="glass-card" isFirstCard={isFirstCard}>
      <DefaultCardImage src={url ?? item.thumbnailUrl} alt="" />
      <DefaultCardContent>
        <SanitizedHTML allowedAttributes={{ span: ['style'] }} html={title} />
      </DefaultCardContent>
    </DefaultCard>
  );
};

const ItemsTinderStack: FC<IItemsTinderStackProps> = ({
  items,
  title,
  className,
  onSwipe,
  onCardLeftScreen,
  preventSwipe = ['up', 'down', 'left'],
  swipeThreshold = 80,
}) => {
  const list = useMemo(() => items ?? [], [items]);
  const [stackKey, setStackKey] = useState(0);
  const [stackItems, setStackItems] = useState<IProviderItemView[]>(list);
  const [currentTopId, setCurrentTopId] = useState<number>();

  useEffect(() => {
    setStackItems(list);

    if (list.length > 0) setCurrentTopId(list.at(0)?.id);
  }, [list]);

  const moveItemToEnd = useCallback((itemId: IProviderItemView['id']) => {
    setStackItems((prev) => {
      const currentIndex = prev.findIndex((el) => el.id === itemId);
      if (currentIndex <= -1 || prev.length <= 1) {
        return prev;
      }
      const next = [...prev];
      const [swipedItem] = next.splice(currentIndex, 1);
      next.push(swipedItem);
      return next;
    });
  }, []);

  const handleCardLeftScreen = useCallback(
    (direction: ItemsTinderSwipeDirection, item: IProviderItemView) => {
      onCardLeftScreen?.(direction, item);
      setStackKey((k) => k + 1);
    },
    [onCardLeftScreen]
  );

  if (!stackItems.length) {
    return null;
  }

  return (
    <StackContainer>
      {title && (
        <StackHeader>
          <SanitizedHTML allowedAttributes={{ span: ['style'] }} html={title} />
        </StackHeader>
      )}
      <StackRoot className={className}>
        {stackItems.map((item, index) => (
          <div
            key={`${item.id}-${stackKey}`}
            className="absolute left-0 top-0 w-full"
            style={{ zIndex: stackItems.length - index }}
          >
            <TinderCard
              className="h-full w-full"
              preventSwipe={preventSwipe}
              swipeRequirementType="position"
              swipeThreshold={swipeThreshold}
              flickOnSwipe
              onSwipe={(direction) => {
                onSwipe?.(direction, item);
                moveItemToEnd(item.id);
              }}
              onCardLeftScreen={(direction) =>
                handleCardLeftScreen(direction, item)
              }
            >
              {defaultRenderCard(
                item,
                currentTopId === item.id && index === 0 && stackKey === 0
              )}
            </TinderCard>
          </div>
        ))}
      </StackRoot>
    </StackContainer>
  );
};

export default ItemsTinderStack;
